import React, { useState } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Avatar,
    Stack,
    useTheme,
    alpha,
    Alert,
    IconButton,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const Profile = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [avatar, setAvatar] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        setFormData(prev => ({
            ...prev,
            username: userInfo.username || ''
        }));
        // Se o usuário já tiver um avatar, carrega ele
        if (userInfo.avatar_url) {
            setPreviewUrl(`${process.env.REACT_APP_API_URL}${userInfo.avatar_url}`);
        }
    }, []);

    const handleAvatarChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Verifica o tamanho do arquivo (máximo 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('A imagem deve ter no máximo 5MB');
                return;
            }
            setAvatar(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            // Validações
            if (formData.newPassword && !formData.password) {
                throw new Error('Para alterar a senha, é necessário informar a senha atual');
            }

            if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
                throw new Error('As senhas não coincidem');
            }

            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            const formDataToSend = new FormData();
            
            // Só envia os campos que foram alterados e não estão vazios
            if (formData.username !== userInfo.username && formData.username.trim()) {
                formDataToSend.append('username', formData.username.trim());
            }
            
            if (formData.newPassword && formData.newPassword.trim()) {
                formDataToSend.append('current_password', formData.password.trim());
                formDataToSend.append('new_password', formData.newPassword.trim());
            }

            if (avatar) {
                formDataToSend.append('avatar', avatar);
            }

            // Se nenhum campo foi alterado, mostra mensagem
            if ([...formDataToSend.entries()].length === 0) {
                setSuccess('Nenhuma alteração foi realizada');
                setLoading(false);
                return;
            }

            // Log dos dados que serão enviados
            console.log('Dados do formulário a serem enviados:');
            for (let [key, value] of formDataToSend.entries()) {
                console.log(`${key}:`, value instanceof File ? `File: ${value.name}` : value);
            }

            console.log('Enviando requisição para atualizar perfil...');
            const response = await api.put('/users/me', formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            console.log('Resposta do servidor:', response.data);
            
            // Atualiza as informações do usuário no localStorage
            const updatedUserInfo = {
                ...userInfo,
                username: response.data.username,
                avatar_url: response.data.avatar_url
            };
            localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));

            setSuccess('Perfil atualizado com sucesso!');
            
            // Limpa os campos de senha
            setFormData(prev => ({
                ...prev,
                password: '',
                newPassword: '',
                confirmPassword: ''
            }));

            // Atualiza a preview do avatar se foi alterado
            if (response.data.avatar_url) {
                setPreviewUrl(`${process.env.REACT_APP_API_URL}${response.data.avatar_url}`);
                
                // Força uma atualização do componente pai (se necessário)
                if (window.dispatchEvent) {
                    window.dispatchEvent(new Event('storage'));
                }
            }

            // Limpa o arquivo de avatar após o upload
            setAvatar(null);
        } catch (error) {
            console.error('Erro ao atualizar perfil:', error);
            
            // Tratamento de erro mais detalhado
            if (error.response) {
                // O servidor respondeu com um status de erro
                const errorData = error.response.data;
                if (errorData.detail) {
                    if (Array.isArray(errorData.detail)) {
                        const errorMessages = errorData.detail
                            .map(err => {
                                if (typeof err === 'object') {
                                    return err.msg || err.loc?.join('.') + ': ' + err.msg || JSON.stringify(err);
                                }
                                return err;
                            })
                            .filter(Boolean)
                            .join('\n');
                        setError(errorMessages || 'Erro ao atualizar perfil');
                    } else {
                        setError(errorData.detail);
                    }
                } else {
                    setError('Erro ao atualizar perfil: ' + error.response.status);
                }
            } else if (error.request) {
                // A requisição foi feita mas não houve resposta
                setError('Não foi possível conectar ao servidor. Verifique sua conexão.');
            } else {
                // Erro na configuração da requisição ou erro de validação local
                setError(error.message || 'Erro ao atualizar perfil. Por favor, tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Typography 
                variant="h4" 
                sx={{ 
                    mb: 4,
                    color: theme.palette.mode === 'dark' ? '#fff' : '#1a1f2c',
                    fontWeight: 600
                }}
            >
                Meu Perfil
            </Typography>

            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.paper, 0.6)
                        : theme.palette.background.paper,
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(0, 0, 0, 0.1)'
                }}
            >
                {error && (
                    <Alert 
                        severity="error" 
                        sx={{ mb: 3 }}
                        onClose={() => setError('')}
                    >
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert 
                        severity="success" 
                        sx={{ mb: 3 }}
                        onClose={() => setSuccess('')}
                    >
                        {success}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            mb: 2 
                        }}>
                            <Box sx={{ position: 'relative' }}>
                                <Avatar
                                    src={previewUrl}
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        mb: 2,
                                        border: '4px solid',
                                        borderColor: theme.palette.primary.main
                                    }}
                                />
                                <input
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    id="avatar-upload"
                                    type="file"
                                    onChange={handleAvatarChange}
                                />
                                <label htmlFor="avatar-upload">
                                    <Tooltip title="Alterar foto (máx. 5MB)">
                                        <IconButton
                                            component="span"
                                            sx={{
                                                position: 'absolute',
                                                right: 0,
                                                bottom: 16,
                                                backgroundColor: theme.palette.primary.main,
                                                color: '#fff',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.dark
                                                }
                                            }}
                                        >
                                            <PhotoCamera />
                                        </IconButton>
                                    </Tooltip>
                                </label>
                            </Box>
                        </Box>

                        <TextField
                            label="Nome de Usuário"
                            value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            fullWidth
                            required
                        />

                        <Alert severity="info" sx={{ mt: 2 }}>
                            Os campos de senha são opcionais. Preencha apenas se desejar alterar sua senha.
                        </Alert>

                        <TextField
                            label="Senha Atual"
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            fullWidth
                            required={!!formData.newPassword}
                            helperText="Necessário apenas se desejar alterar a senha"
                        />

                        <TextField
                            label="Nova Senha"
                            type="password"
                            value={formData.newPassword}
                            onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                            fullWidth
                        />

                        <TextField
                            label="Confirmar Nova Senha"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                            fullWidth
                            error={formData.newPassword !== formData.confirmPassword}
                            helperText={
                                formData.newPassword !== formData.confirmPassword 
                                    ? "As senhas não coincidem" 
                                    : ""
                            }
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                            sx={{
                                py: 1.5,
                                mt: 2,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontSize: '1rem'
                            }}
                        >
                            {loading ? 'Salvando...' : 'Salvar Alterações'}
                        </Button>
                    </Stack>
                </form>
            </Paper>
        </Container>
    );
};

export default Profile; 